import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash2, X, Shield } from "lucide-react";

const ROLE_COLORS = {
  hotel_admin: "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/25",
  property_manager: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
  front_desk: "bg-blue-400/10 text-blue-300 border-blue-400/25",
  housekeeping_supervisor: "bg-purple-400/10 text-purple-300 border-purple-400/25",
  housekeeping_staff: "bg-white/5 text-white/60 border-white/10",
  revenue_manager: "bg-pink-400/10 text-pink-300 border-pink-400/25",
  accountant: "bg-orange-400/10 text-orange-300 border-orange-400/25",
  super_admin: "bg-red-400/10 text-red-300 border-red-400/25",
};

const ROLE_LABELS = {
  hotel_admin: "Hotel Admin",
  property_manager: "Property Manager",
  front_desk: "Front Desk",
  housekeeping_supervisor: "Housekeeping Supervisor",
  housekeeping_staff: "Housekeeping Staff",
  revenue_manager: "Revenue Manager",
  accountant: "Accountant",
  super_admin: "Super Admin",
};

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/team"); setData(data); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function changeRole(id, role) {
    try { await api.patch(`/team/${id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  async function remove(id, name) {
    if (!confirm(`Remove ${name}? This cannot be undone.`)) return;
    try { await api.delete(`/team/${id}`); toast.success("Member removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  if (loading || !data) return <div className="text-white/50">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="team-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Team</div>
          <h1 className="font-display text-3xl md:text-4xl">Users &amp; Roles</h1>
          <p className="text-white/55 text-sm mt-2">Invite teammates and assign role-based permissions across the property.</p>
        </div>
        {data.can_manage && (
          <button onClick={() => setInviting(true)} className="btn-gold rounded-md px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="invite-btn">
            <UserPlus className="w-4 h-4" /> Invite member
          </button>
        )}
      </div>

      <div className="bg-[#12182B] border border-white/5 rounded-xl overflow-hidden" data-testid="team-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/50 text-xs uppercase tracking-widest border-b border-white/5">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Email</th>
                <th className="px-5 py-4">Role</th>
                <th className="px-5 py-4">Joined</th>
                <th className="px-5 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.id} className="border-b border-white/5 last:border-none hover:bg-[#1A2235]" data-testid={`member-${m.id}`}>
                  <td className="px-5 py-4">
                    <div className="font-medium">{m.name}</div>
                    {m.id === user.id && <div className="text-[10px] text-[#D4AF37] uppercase tracking-widest">You</div>}
                  </td>
                  <td className="px-5 py-4 text-white/70">{m.email}</td>
                  <td className="px-5 py-4">
                    {data.can_manage && m.id !== user.id ? (
                      <select value={m.role || "hotel_admin"} onChange={(e) => changeRole(m.id, e.target.value)} className={`text-[11px] tracking-widest uppercase px-2 py-1 rounded border bg-transparent ${ROLE_COLORS[m.role] || "bg-white/5 text-white/60 border-white/10"}`} data-testid={`role-select-${m.id}`}>
                        {data.roles.map((r) => <option key={r.value} value={r.value} className="bg-[#0a0f1c] text-white">{r.label}</option>)}
                      </select>
                    ) : (
                      <span className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded border ${ROLE_COLORS[m.role] || "bg-white/5 text-white/60 border-white/10"}`}>{ROLE_LABELS[m.role] || m.role}</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-white/60">{m.created_at ? new Date(m.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                  <td className="px-5 py-4 text-right">
                    {data.can_manage && m.id !== user.id && (
                      <button onClick={() => remove(m.id, m.name)} className="text-xs text-orange-400 hover:text-orange-300 inline-flex items-center gap-1" data-testid={`remove-${m.id}`}>
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {inviting && <InviteDialog roles={data.roles} onClose={() => setInviting(false)} onDone={() => { setInviting(false); load(); }} />}
    </div>
  );
}

function InviteDialog({ roles, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", role: "front_desk" });
  const [loading, setLoading] = useState(false);
  const [tempPass, setTempPass] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/team/invite", form);
      setTempPass(data.temp_password);
      toast.success(`Invited ${form.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose} data-testid="invite-dialog">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#12182B] border border-white/10 rounded-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D4AF37]">Invite member</div>
            <div className="font-display text-xl mt-1">Add a teammate</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        {!tempPass ? (
          <>
            <div className="space-y-4">
              <Field label="Full name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="invite-name" /></Field>
              <Field label="Email"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" data-testid="invite-email" /></Field>
              <Field label="Role">
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input" data-testid="invite-role">
                  {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </Field>
            </div>
            <button type="submit" disabled={loading} className="btn-gold w-full rounded-md py-3 mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="invite-submit">
              <Shield className="w-4 h-4" /> {loading ? "Inviting…" : "Send invite"}
            </button>
          </>
        ) : (
          <div className="bg-[#0a0f1c] border border-[#D4AF37]/30 rounded-md p-4" data-testid="invite-success">
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2">Invitation sent to {form.email}</div>
            <div className="text-sm text-white/70 mb-3">Share these temporary credentials (also emailed):</div>
            <div className="text-xs font-mono bg-black/30 rounded p-3 text-white/90">
              Email: {form.email}<br/>
              Password: <strong className="text-[#D4AF37]">{tempPass}</strong>
            </div>
            <button type="button" onClick={onDone} className="btn-gold w-full rounded-md py-2.5 mt-4 text-sm">Done</button>
          </div>
        )}
        <style>{`.input { width: 100%; background: #0a0f1c; border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 10px 12px; color: #F8F9FA; font-size: 14px; outline: none; } .input:focus { border-color: rgba(212, 175, 55, 0.6); }`}</style>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
      {children}
    </div>
  );
}
